<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <h2 class="text-3xl font-display font-bold text-white">{{ $t('auth.reset_password') }}</h2>
      <p class="mt-2 text-sm text-gray-400">
        {{ $t('auth.reset_password_desc') || 'Enter your email to receive instructions' }}
      </p>
    </div>

    <!-- Form -->
    <form @submit.prevent="handleForgotPassword" class="space-y-6">
      <!-- Email -->
      <div>
        <label for="email" class="form-label">
          {{ $t('auth.email') }}
        </label>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <EnvelopeIcon class="h-5 w-5 text-gray-500" />
          </div>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            class="form-input pl-10"
            :class="{ 'border-red-500/50 focus:ring-red-500': errors.email }"
            placeholder="you@example.com"
          />
        </div>
        <p v-if="errors.email" class="mt-1 text-sm text-red-400">{{ errors.email[0] }}</p>
      </div>

      <!-- Submit button -->
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full btn btn-lg btn-primary bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 border-none"
        :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
      >
        <div v-if="isLoading" class="flex items-center">
          <div class="spinner w-5 h-5 mr-2 border-white"></div>
          {{ $t('common.messages.loading') }}
        </div>
        <span v-else class="flex items-center">
          {{ $t('auth.send_reset_link') || 'Send Reset Link' }}
          <PaperAirplaneIcon class="ml-2 h-4 w-4" />
        </span>
      </button>
    </form>

    <!-- Back to login -->
    <p class="text-center text-sm text-gray-400">
      <router-link
        :to="{ name: 'login' }"
        class="font-medium text-blue-400 hover:text-blue-300 transition-colors flex items-center justify-center"
      >
        <ArrowLeftIcon class="w-4 h-4 mr-1" />
        {{ $t('auth.back_to_login') || 'Back to Login' }}
      </router-link>
    </p>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon
} from '@heroicons/vue/24/outline'

const { t } = useI18n()
const authStore = useAuthStore()
const toast = useToast()

const form = reactive({
  email: ''
})

const errors = ref({})
const isLoading = ref(false)

const handleForgotPassword = async () => {
  isLoading.value = true
  errors.value = {}

  try {
    await authStore.forgotPassword(form)
    toast.success(t('auth.reset_link_sent'))
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors || {}
    } else {
      toast.error(t('common.messages.error'))
    }
  } finally {
    isLoading.value = false
  }
}
</script>